import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { RatingBadge } from "@/components/shared/rating-scale";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useConstraintLevels, useConstraintValues } from "@/hooks/use-constraints";
import {
  type PersonConstraint,
  useAddPersonConstraint,
  usePersonConstraints,
  useRemovePersonConstraint,
  useUpdatePersonConstraint,
} from "@/hooks/use-person-constraints";
import {
  type AddPersonConstraintInput,
  addPersonConstraintSchema,
} from "@/schemas/person-constraints";

type DialogState =
  | { type: "none" }
  | { type: "add" }
  | { type: "edit"; row: PersonConstraint }
  | { type: "remove"; row: PersonConstraint };

/**
 * A person's staffing constraints — what they're familiar with, grouped by type.
 *
 * **The whole section is restricted** to the admin tier, the person themselves
 * and their direct manager — the route refuses anyone else outright as of
 * 2026-09-22. It used to be open with only the 1-5 rating redacted, and this
 * comment used to say so; HR asked a second time and the stricter reading won.
 *
 * The server still redacts `level` independently, so this component renders an
 * absence it did not decide — it must never gate on `canViewRatings` to hide
 * something the payload still contains.
 *
 * Only admins can set a rating: these are set at leadership / People Ops level,
 * not by a line manager, which is why `canManage` is narrower than the audience
 * that can *see* the number.
 */
export function PersonConstraintsSection({
  personId,
  personName,
}: {
  personId: string;
  personName: string;
}) {
  const { data, isLoading, isError, error, refetch } = usePersonConstraints(personId);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const remove = useRemovePersonConstraint(personId);

  const rows = useMemo(() => data?.constraints ?? [], [data]);
  const groups = useMemo(() => {
    const byType = new Map<string, { typeName: string; items: PersonConstraint[] }>();
    for (const row of rows) {
      const g = byType.get(row.typeKey) ?? { typeName: row.typeName, items: [] };
      g.items.push(row);
      byType.set(row.typeKey, g);
    }
    return [...byType.entries()].map(([typeKey, g]) => ({ typeKey, ...g }));
  }, [rows]);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load constraints"}
        onRetry={() => refetch()}
      />
    );
  }

  const canManage = data?.canManage ?? false;
  const canViewRatings = data?.canViewRatings ?? false;

  return (
    <DetailSection
      title="Constraints"
      headerActions={
        canManage && (
          <Button variant="ghost" size="sm" onClick={() => setDialog({ type: "add" })}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add
          </Button>
        )
      }
    >
      <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
        What {personName} is familiar with — industries, technologies and kinds of client. This is
        familiarity, not performance: a low rating means limited exposure to something, never that
        someone is falling short. Anything not listed simply hasn't been rated.
        {!canViewRatings && rows.length > 0 && (
          <> Ratings themselves are only visible to admins, {personName}, and their manager.</>
        )}
      </p>

      {isLoading ? (
        <LoadingRows />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          title="No constraints recorded"
          description={
            canManage
              ? "Add the industries and technologies this person is familiar with."
              : "Nothing has been recorded for this person yet."
          }
        />
      ) : (
        /* Types side by side rather than stacked.
           **Two columns, not four.** A row here carries a name, a rating with
           its wording, and two icon buttons, which needs about 350px before the
           name starts wrapping — and the tab is ~1140px wide. An `auto-fit`
           track was tried first and gave three columns with the fourth type
           orphaned onto a second row, every name wrapped. Two columns fit the
           four types as a tidy 2x2 with room to read them.
           `items-start` keeps a short group from stretching to the height of a
           long one. */
        <div className="grid items-start gap-x-6 gap-y-5 md:grid-cols-2">
          {groups.map((group) => (
            /* Each type is a CARD with a tinted header rather than a bare label
               over a hairline list. This section sits inside a white panel on a
               white page, so an `--border` hairline (#e6ebf2) was the only thing
               separating anything from anything and the whole tab read flat.
               `bg-muted` on the header is the same device the Settings config
               page and the constraints report already use to anchor a list. */
            <div
              key={group.typeKey}
              className="min-w-0 overflow-hidden rounded-lg border border-border shadow-sm"
            >
              <div className="flex items-center justify-between gap-2 border-border border-b bg-muted px-3 py-2">
                <span className="font-semibold text-foreground text-xs uppercase tracking-wide">
                  {group.typeName}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {group.items.length}
                </span>
              </div>
              <ul className="divide-y divide-border">
                {group.items.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-accent/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{row.name}</span>
                        {row.isHidden && <StatusPill tone="neutral" label="Hidden" />}
                        {/* Distinct from hidden: the value is gone from NOS, so
                            nobody can act on it and the name shown is the one
                            recorded when the rating was set. */}
                        {row.isMissingFromNos && <StatusPill tone="neutral" label="Not in NOS" />}
                      </div>
                      {row.notes && (
                        <p className="mt-0.5 text-muted-foreground text-xs">{row.notes}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {row.level === null ? (
                        // Redacted by the server. Say so rather than showing a
                        // blank that reads as "not rated" — the two are
                        // different facts and only one of them is true.
                        <span className="text-muted-foreground text-xs italic">Not shown</span>
                      ) : (
                        <RatingBadge level={row.level} name={row.levelName} />
                      )}
                    </div>
                    {canManage && (
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={`Edit ${row.name} rating`}
                          onClick={() => setDialog({ type: "edit", row })}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label={`Remove ${row.name}`}
                          onClick={() => setDialog({ type: "remove", row })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {(dialog.type === "add" || dialog.type === "edit") && (
        <PersonConstraintDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          personId={personId}
          row={dialog.type === "edit" ? dialog.row : undefined}
          alreadyHeld={rows.map((r) => `${r.typeKey}:${r.valueId}`)}
        />
      )}

      {dialog.type === "remove" && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog({ type: "none" })}
          title={`Remove "${dialog.row.name}"?`}
          description={`This takes the constraint off ${personName}. Removing it is how "no familiarity" is recorded — there is no zero rating.`}
          confirmLabel="Remove"
          destructive
          onConfirm={() =>
            remove.mutate(dialog.row.id, { onSuccess: () => setDialog({ type: "none" }) })
          }
        />
      )}
    </DetailSection>
  );
}

function PersonConstraintDialog({
  open,
  onOpenChange,
  personId,
  row,
  alreadyHeld,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
  row?: PersonConstraint;
  alreadyHeld: string[];
}) {
  const isEdit = row != null;
  const add = useAddPersonConstraint(personId);
  const update = useUpdatePersonConstraint(personId);
  const { data: values } = useConstraintValues(!isEdit);
  const { data: levels = [] } = useConstraintLevels();

  // Only offer values the person doesn't already hold, under an active grouping,
  // and not hidden in Enable — the server refuses a duplicate, and an option that
  // can only fail is worse than absent. A HIDDEN value is excluded here and only
  // here: hiding stops new ratings, it never touches the ones already recorded.
  //
  // **A grouping with nothing left to offer is dropped entirely**, rather than
  // being pickable and then showing an empty second list — choosing it would be
  // a dead end the user only discovers after committing to it.
  const held = new Set(alreadyHeld);
  const selectable = (values?.groups ?? [])
    .filter((g) => g.isActive)
    .map((g) => ({
      key: g.key,
      label: g.label,
      values: g.values.filter((v) => !v.hidden && !held.has(`${g.key}:${v.id}`)),
    }))
    .filter((g) => g.values.length > 0);

  const form = useForm<AddPersonConstraintInput>({
    resolver: zodResolver(addPersonConstraintSchema),
    defaultValues: {
      typeKey: row?.typeKey ?? "",
      valueId: row?.valueId ?? "",
      level: row?.level ?? undefined,
      notes: row?.notes ?? "",
    },
  });

  const chosenType = form.watch("typeKey");
  const valuesForType = selectable.find((g) => g.key === chosenType)?.values ?? [];

  async function onSubmit(values: AddPersonConstraintInput) {
    if (isEdit) {
      // The constraint itself is not re-targetable — only the rating and note.
      await update.mutateAsync({ id: row.id, level: values.level, notes: values.notes });
    } else {
      await add.mutateAsync(values);
    }
    form.reset();
    onOpenChange(false);
  }

  const pending = add.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Rate ${row.name}` : "Add a constraint"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Change the rating or the note. To record a different constraint, add it separately."
              : "Pick something this person is familiar with, and how familiar they are."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {!isEdit && (
              <FormField
                control={form.control}
                name="typeKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select
                      onValueChange={(v) => {
                        field.onChange(v);
                        // The chosen value belongs to the type you left, so drop
                        // it rather than submit a mismatched pair the server
                        // would refuse.
                        form.setValue("valueId", "");
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose a type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {selectable.map((g) => (
                          <SelectItem key={g.key} value={g.key}>
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {values?.connected === false
                        ? "Enable can't reach NOS right now, so the list is unavailable."
                        : "These come from NOS. Hide ones you don't want at Settings → Config → Constraints."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {!isEdit && (
              <FormField
                control={form.control}
                name="valueId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Constraint</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!chosenType}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={chosenType ? "Choose a constraint" : "Pick a type first"}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {valuesForType.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Familiarity</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value ? String(field.value) : ""}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a rating" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {levels.map((l) => (
                        <SelectItem key={l.id} value={String(l.ordinal)}>
                          {l.ordinal} — {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Familiarity, not performance. Someone with none of this simply isn't listed
                    against it.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={2}
                      placeholder="Optional — context for the rating."
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>Visible to the same people as the rating.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
